const HARDCOVER_URL = 'https://api.hardcover.app/v1/graphql';

function gql(query, variables = {}) {
  const token = process.env.REACT_APP_HARDCOVER_TOKEN;
  return fetch(HARDCOVER_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ query, variables }),
  }).then(r => r.json());
}

// results is a JSON scalar — Hardcover returns it as either an object or a
// stringified object depending on the client version. Parse defensively.
function parseResults(raw) {
  if (!raw) return null;
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
}

export async function findBook(title, author) {
  const query = `
    query FindBook($query: String!) {
      search(query: $query, query_type: "Book", per_page: 5) {
        results
      }
    }
  `;
  try {
    const data = await gql(query, { query: `${title} ${author}` });
    const parsed = parseResults(data?.data?.search?.results);
    const hits = parsed?.hits ?? [];
    if (!hits.length) return null;

    // Edition entries have slugs like "title-by-author" and carry only 1 rating.
    // Canonical entries (just "title") hold the real aggregate. Prefer those.
    const sorted = [...hits].sort((a, b) => {
      const aIsEdition = a.document?.slug?.includes('-by-') ? 1 : 0;
      const bIsEdition = b.document?.slug?.includes('-by-') ? 1 : 0;
      return aIsEdition - bIsEdition;
    });

    const doc = sorted[0].document;
    return {
      id: doc.id,
      slug: doc.slug,
      title: doc.title,
      author: doc.author_names?.[0] ?? author,
    };
  } catch {
    return null;
  }
}

// Fetch public reviews for a book by Hardcover book id.
// Uses user_books (not user_book_reads which doesn't exist).
export async function fetchBookReviews(bookId) {
  const query = `
    query BookReviews($bookId: Int!) {
      user_books(
        where: {
          book_id: { _eq: $bookId }
          review: { _is_null: false, _neq: "" }
        }
        limit: 8
        order_by: { updated_at: desc }
      ) {
        review
        rating
        user {
          username
        }
      }
    }
  `;
  try {
    const data = await gql(query, { bookId: parseInt(bookId, 10) });
    const rows = data?.data?.user_books ?? [];
    return rows
      .map(r => ({
        review: r.review,
        rating: r.rating,
        username: r.user?.username,
      }))
      .filter(r => r.review?.trim().length > 20);
  } catch {
    return [];
  }
}

// Find a book then return its reviews in one shot.
export async function fetchReviewsForBook(title, author) {
  try {
    const book = await findBook(title, author);
    if (!book) return [];
    return await fetchBookReviews(book.id);
  } catch {
    return [];
  }
}

// Fetch the aggregate community rating via a server-side proxy to avoid CORS.
// Returns { rating, ratingsCount } or null on failure.
export async function fetchBookRating(title, author) {
  try {
    const res = await fetch('/.netlify/functions/hardcover-rating', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, author }),
    });
    if (!res.ok) return null;
    return await res.json(); // null or { rating, ratingsCount }
  } catch {
    return null;
  }
}
